import {AUDIO_PENDING_WINDOW_MS, AUDIO_REACT_PENDING_WINDOW_MS} from '../config'
import type {AudioConfig, SoundConfig} from '../content/manifest.types'
import {describeError} from '../util/errors'
import type {AudioUnlock} from './audio-unlock'
import type {MuteState} from './mute-state'

/**
 * Every sound the experience makes, loaded from `campaign.json` and played by
 * name.
 *
 * One bank rather than a player per cue. The moments that make noise -
 * footstep, arrival, shutter, and the tap reaction keyed to whichever clip the
 * draw landed on - differ only in which buffer they reach for and how late they
 * are allowed to be, and a module per moment to express that was that many
 * copies of the same fetch, the same magic-byte sniff and the same unlock dance.
 *
 * RAW WEB AUDIO, NOT `THREE.Audio`, and that is the substantive change from the
 * single-file player this replaced. A fresh `AudioBufferSourceNode` per play
 * means overlapping cues cost nothing, which the footsteps need: they are
 * 160 ms apart, and `THREE.Audio` refuses to start while `isPlaying`, so the old
 * player had to `stop()` first - i.e. step two cut step one off. Nothing here is
 * positional either, so the PannerNode-shaped API bought nothing in exchange,
 * and dropping it also drops the `@types/three` AudioContext quirk documented in
 * the README.
 *
 * Everything is best-effort and NEVER throws at the user. `load` resolves with
 * warnings instead of rejecting, which is what makes it structurally impossible
 * for a missing sound file to surface as an error message over the camera feed;
 * `play` on a slot that failed, or was never configured, is a no-op.
 *
 * The fetch is done by hand rather than through `THREE.AudioLoader`, mirroring
 * `model/loader.ts`: AudioLoader discards the HTTP status and swallows failures
 * into `console.error`, which is the opposite of what a swap-time mistake needs.
 */

// Spelled out rather than `keyof AudioConfig`: `react` is a keyed map, not a
// slot, and it enters the bank through `playReaction` below.
export type SoundSlot = 'step' | 'arrive' | 'shutter'

const SLOTS: readonly SoundSlot[] = ['step', 'arrive', 'shutter']

/**
 * The internal key for one react cue. Namespaced so a GLB clip that happens to
 * be called `arrive` or `step` can never collide with a fixed slot in the maps
 * below.
 */
const reactKey = (clipName: string): string => `react:${clipName}`

/**
 * How long a cue may wait for the AudioContext to unlock before it is dropped.
 *
 * Per slot, because "late" means different things to each of them:
 *
 *  - `arrive` confirms a placement, and confirming it a beat late still confirms
 *    it. On the first tap of an iOS session this is the difference between a
 *    silent placement and a slightly delayed one.
 *  - `step` is one moment inside a 0.58 s gait. A footstep that lands 300 ms
 *    after its paw print is not a late footstep, it is a wrong one, so it is
 *    dropped instead. The first of the three is the likeliest casualty - iOS
 *    `pointerdown` carries no activation - and it is also the quietest, so it is
 *    the cheapest one to lose.
 *  - `shutter` rides a `click`, which reliably carries activation. Anything that
 *    could queue here has already failed for a different reason.
 *
 * React cues get their own, shorter window (`AUDIO_REACT_PENDING_WINDOW_MS`,
 * see its comment in config.ts): normally the placement gesture unlocked the
 * context long before a model tap is possible, so the only race left to bridge
 * is the first tap after an iOS interruption.
 */
const QUEUE_WINDOW_MS: Record<SoundSlot, number> = {
  step: 0,
  arrive: AUDIO_PENDING_WINDOW_MS,
  shutter: 0,
}

// `slot` is a plain string rather than `SoundSlot`: react cues warn under
// their internal key, so a broken tap sound reads `react:Happy: fetch ...`.
export type SoundWarning =
  | {readonly kind: 'fetchFailed'; readonly slot: string; readonly url: string; readonly reason: string}
  | {readonly kind: 'httpError'; readonly slot: string; readonly url: string; readonly status: number}
  | {readonly kind: 'servedHtml'; readonly slot: string; readonly url: string; readonly contentType: string}
  | {readonly kind: 'unknownFormat'; readonly slot: string; readonly url: string; readonly head: string}
  | {readonly kind: 'decodeFailed'; readonly slot: string; readonly url: string; readonly reason: string}
  | {readonly kind: 'stereoSource'; readonly slot: string; readonly url: string}

export const describeSoundWarning = (warning: SoundWarning): string => {
  switch (warning.kind) {
    case 'fetchFailed':
      return `${warning.slot}: fetch ${warning.url} failed (${warning.reason}); that cue is silent`
    case 'httpError':
      return `${warning.slot}: ${warning.url} returned ${warning.status}; that cue is silent`
    case 'servedHtml':
      return `${warning.slot}: ${warning.url} served ${warning.contentType} instead of audio; check the path`
    case 'unknownFormat':
      return `${warning.slot}: ${warning.url} is not mp3/m4a/wav (header ${warning.head}); that cue is silent`
    case 'decodeFailed':
      return `${warning.slot}: ${warning.url} could not be decoded (${warning.reason}); that cue is silent`
    case 'stereoSource':
      return `${warning.slot}: ${warning.url} is stereo; mono halves the bytes and sounds identical here`
  }
}

export interface SoundSlotDebug {
  readonly url: string | null
  readonly ready: boolean
  readonly durationSeconds: number
  readonly channels: number
}

export interface SoundBankDebug {
  /** `'none'` means no AudioContext has been constructed yet. */
  readonly state: AudioContextState | 'none'
  readonly sampleRate: number
  /** The shared `MuteState` flag, i.e. the master gain is at 0 when true. */
  readonly muted: boolean
  /**
   * The three fixed slots always appear (`url: null` when unconfigured);
   * `react:*` keys appear only when the campaign named them, because a clip the
   * campaign never mentioned is not a slot that exists.
   */
  readonly slots: Readonly<Record<string, SoundSlotDebug>>
}

export interface PlayOptions {
  /** Multiplies the slot's configured volume. Used by the footstep weights. */
  readonly gain?: number
  /** `playbackRate`. Slight per-step variation is what stops three plays of one
   * file reading as a sample being retriggered. */
  readonly rate?: number
}

export interface SoundBank {
  /**
   * NEVER rejects. Problems come back as warnings and the affected slot runs
   * silent; slots the campaign did not configure are skipped without comment.
   * With nothing configured at all, no AudioContext is ever constructed.
   */
  load(config: AudioConfig, signal?: AbortSignal): Promise<readonly SoundWarning[]>
  /** Fire and forget. A no-op for a slot with no buffer. */
  play(slot: SoundSlot, options?: PlayOptions): void
  /**
   * The react cue for one clip, by the clip's name. Same contract as `play`:
   * a clip the campaign gave no sound - or whose file failed to load - is a
   * silent no-op, never an error.
   */
  playReaction(clipName: string, options?: PlayOptions): void
  readonly debug: SoundBankDebug
  dispose(): void
}

/** Same posture as the GLB magic-byte guard: prove the bytes, ignore the extension. */
const sniffFormat = (bytes: ArrayBuffer): 'mp3' | 'm4a' | 'wav' | null => {
  if (bytes.byteLength < 12) return null
  const head = new Uint8Array(bytes, 0, 12)
  // noUncheckedIndexedAccess: reads off a typed array are `number | undefined`.
  const at = (index: number): number => head[index] ?? 0
  const ascii = (start: number, text: string): boolean =>
    [...text].every((character, offset) => at(start + offset) === character.charCodeAt(0))

  if (ascii(0, 'ID3')) return 'mp3'
  // Bare MPEG frame: 11 sync bits set, version not the reserved encoding and
  // layer not the reserved one. Without those last two nearly any file starting
  // 0xFF 0xE? would pass, which is worse than not checking.
  if (at(0) === 0xff && (at(1) & 0xe0) === 0xe0 && (at(1) & 0x18) !== 0x08 && (at(1) & 0x06) !== 0x00) {
    return 'mp3'
  }
  if (ascii(4, 'ftyp')) return 'm4a'
  if (ascii(0, 'RIFF') && ascii(8, 'WAVE')) return 'wav'
  return null
}

const hexHead = (bytes: ArrayBuffer): string =>
  [...new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength))]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join(' ')

interface Loaded {
  readonly url: string
  readonly volume: number
  readonly buffer: AudioBuffer
}

/**
 * A cue that arrived before the context was running, with the clock it is
 * racing. The window is resolved at queue time because react cues share one
 * constant across many keys - there is no per-key record to look it up in
 * later.
 */
interface Pending {
  readonly key: string
  readonly options: PlayOptions | undefined
  readonly at: number
  readonly windowMs: number
}

export const createSoundBank = (getUnlock: () => AudioUnlock, muteState: MuteState): SoundBank => {
  let unlocker: AudioUnlock | null = null
  let disposed = false
  // Keyed by slot name for the fixed slots and by `reactKey(clip)` for the
  // react cues - one map, because past this point they are the same thing.
  const loaded = new Map<string, Loaded>()
  const urls = new Map<string, string>()
  let pending: Pending | null = null
  /**
   * The one node every play routes through, so "muted" is a property of a
   * single gain rather than a check sprinkled over the fire path. That is what
   * makes the unlock queue safe to leave alone: a cue held for the unlock and
   * released while muted just drains through a zero gain, and a cue already
   * sounding goes quiet the moment the toggle flips - neither needs code.
   */
  let master: GainNode | null = null
  let unsubscribeMute: (() => void) | null = null

  const fire = (context: AudioContext, key: string, options: PlayOptions | undefined): void => {
    const entry = loaded.get(key)
    if (entry === undefined) return

    const source = context.createBufferSource()
    source.buffer = entry.buffer
    if (options?.rate !== undefined) source.playbackRate.value = options.rate

    const gain = context.createGain()
    // Written directly rather than through setTargetAtTime: the value never
    // changes for the life of the node, and a ramp against a clock that was
    // frozen while the context was suspended would fade the first ~30 ms in.
    gain.gain.value = entry.volume * (options?.gain ?? 1)

    source.connect(gain).connect(master ?? context.destination)
    source.start()
    // Buffer sources are single-use, so this is the whole lifecycle. Overlapping
    // plays each get their own node and none of them interferes with another,
    // which is the property the footsteps are built on.
    source.onended = () => {
      source.disconnect()
      gain.disconnect()
    }
  }

  /**
   * Fires whatever was waiting on the unlock, if it is still worth firing.
   *
   * Only ever one cue is held. A queue of them would replay the past on unlock -
   * three footsteps and an arrival all at once - which is worse than the silence
   * it is trying to avoid.
   */
  const onStateChange = (): void => {
    const held = pending
    if (held === null || unlocker === null || !unlocker.isRunning) return
    pending = null
    if (performance.now() - held.at <= held.windowMs) {
      fire(unlocker.context, held.key, held.options)
    }
  }

  /** The unlock dance `play` and `playReaction` share; only the window differs. */
  const trigger = (key: string, windowMs: number, options?: PlayOptions): void => {
    if (disposed || unlocker === null || !loaded.has(key)) return
    unlocker.unlock()
    if (!unlocker.isRunning) {
      // Still locked. On iOS only the matching pointerup can unlock, tens of
      // ms from now - so hold it if this cue can survive the wait, and drop it
      // if it cannot. Zero window means drop, without recording anything.
      if (windowMs > 0) pending = {key, options, at: performance.now(), windowMs}
      return
    }
    fire(unlocker.context, key, options)
  }

  const loadSlot = async (
    slot: string,
    config: SoundConfig,
    context: AudioContext,
    signal?: AbortSignal
  ): Promise<readonly SoundWarning[]> => {
    const {url} = config
    urls.set(slot, url)

    let response: Response
    try {
      response = await fetch(url, signal ? {signal} : undefined)
    } catch (cause) {
      // A detach mid-flight is not a problem worth reporting.
      if (signal?.aborted === true) return []
      return [{kind: 'fetchFailed', slot, url, reason: describeError(cause)}]
    }

    if (!response.ok) return [{kind: 'httpError', slot, url, status: response.status}]

    // Workers runs with `not_found_handling: "none"`, so a missing file is a
    // real 404. This catches the other shape of the same typo: a path that
    // resolves to a page.
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('text/html')) {
      return [{kind: 'servedHtml', slot, url, contentType}]
    }

    const bytes = await response.arrayBuffer()
    if (sniffFormat(bytes) === null) {
      return [{kind: 'unknownFormat', slot, url, head: hexHead(bytes)}]
    }

    let buffer: AudioBuffer
    try {
      // Decoding is exempt from the autoplay policy, so this works while the
      // context is still suspended - which is the whole reason the files are
      // fetched at manifest time rather than on the first cue.
      // decodeAudioData detaches the ArrayBuffer; nothing reuses it.
      buffer = await context.decodeAudioData(bytes)
    } catch (cause) {
      if (signal?.aborted === true) return []
      return [{kind: 'decodeFailed', slot, url, reason: describeError(cause)}]
    }

    loaded.set(slot, {url, volume: config.volume, buffer})
    return buffer.numberOfChannels > 1 ? [{kind: 'stereoSource', slot, url}] : []
  }

  return {
    load: async (config, signal) => {
      const configured: (readonly [string, SoundConfig])[] = [
        ...SLOTS.flatMap((slot) => {
          const entry = config[slot]
          return entry === null ? [] : [[slot, entry] as const]
        }),
        ...Object.entries(config.react).map(
          ([clip, entry]) => [reactKey(clip), entry] as const
        ),
      ]
      // The early return is the load-bearing part: a campaign that names no
      // files must not build an audio unit, and this is the only place that
      // decision is made. React entries joined the list above, so a campaign
      // with ONLY tap sounds still gets its context.
      if (configured.length === 0) return []

      unlocker = getUnlock()
      unlocker.context.addEventListener(
        'statechange',
        onStateChange,
        signal ? {signal} : undefined
      )

      const context = unlocker.context
      // Built here, after the early return above, so a silent campaign still
      // constructs no audio unit at all. Written directly rather than ramped,
      // for the frozen-clock reason on the per-play gain in `fire`.
      master = context.createGain()
      master.gain.value = muteState.muted ? 0 : 1
      master.connect(context.destination)
      unsubscribeMute = muteState.subscribe((muted) => {
        if (master !== null) master.gain.value = muted ? 0 : 1
      })
      const results = await Promise.all(
        configured.map(([slot, entry]) => loadSlot(slot, entry, context, signal))
      )
      return results.flat()
    },

    play: (slot, options) => trigger(slot, QUEUE_WINDOW_MS[slot], options),

    playReaction: (clipName, options) =>
      trigger(reactKey(clipName), AUDIO_REACT_PENDING_WINDOW_MS, options),

    get debug(): SoundBankDebug {
      const slot = (name: string): SoundSlotDebug => {
        const entry = loaded.get(name)
        return {
          url: urls.get(name) ?? null,
          ready: entry !== undefined,
          durationSeconds: entry?.buffer.duration ?? 0,
          channels: entry?.buffer.numberOfChannels ?? 0,
        }
      }
      // Fixed slots first and always; react keys only as configured. `urls`
      // rather than `loaded` so a cue that failed to decode still shows up,
      // with `ready: false` - which is the answer being asked for.
      const names = [...SLOTS, ...[...urls.keys()].filter((key) => !SLOTS.includes(key as SoundSlot))]
      return {
        state: unlocker?.context.state ?? 'none',
        sampleRate: unlocker?.context.sampleRate ?? 0,
        muted: muteState.muted,
        slots: Object.fromEntries(names.map((name) => [name, slot(name)])),
      }
    },

    dispose: () => {
      disposed = true
      pending = null
      loaded.clear()
      urls.clear()
      // The unlock is borrowed, not owned - the owner tears it down once for
      // every cue that shares it. Only this bank's own listener comes off here,
      // and the context is deliberately NOT closed: THREE memoises it
      // module-globally and onDetach can fire while the page lives on.
      unlocker?.context.removeEventListener('statechange', onStateChange)
      unlocker = null
      unsubscribeMute?.()
      unsubscribeMute = null
      // Because the context outlives the bank, the master gain must come off
      // destination here or every attach/detach cycle would leave one more
      // orphaned node connected for the life of the page.
      master?.disconnect()
      master = null
    },
  }
}
